<template>
    <div>
        <ContentComp
            :loadState="LoadState"
            :showAsModal="true"
        >
            <Dropzone
                ref="dropzone"
                v-if="data.length == 0"
                @change="handleFileList"
                class="border border-2"
            ></Dropzone>

            <div
                v-else
                class="d-flex gap-3 flex-column"
            >
                <div class="d-flex flex-column gap-2">
                    <p v-html="$tm('trackingimport.importinformation', '', true)"></p>

                    <label
                        >{{ $tm('trackingimport.organizer') }} <TooltipHelp text="trackingimport.organizerhelp" />
                        <InputOrganizerSelect v-model="currentOrganizerId" />
                    </label>
                </div>

                <div class="d-flex gap-2 mt-5">
                    <ButtonComp
                        button-type="Delete"
                        @click="deleteSelected"
                        >{{ $tm('button.delete') }}</ButtonComp
                    >
                </div>
                <GridComp
                    v-if="data.length > 0"
                    ref="grid"
                    :grid-setup="gridSetup"
                />
            </div>
        </ContentComp>
    </div>
</template>

<script setup lang="ts">
import { ButtonComp, ContentComp, DateComp, Dropzone, GridComp, InputOrganizerSelect, TooltipHelp } from '@/components';
import DisplayColor from '@/components/Basic/DisplayColor.vue';
import DisplayDistance from '@/components/Basic/DisplayDistance.vue';
import { GridColumnDefinition, GridColumnOptions, GridDataSource, GridSetup } from '@/components/Grid/Classes';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { useTaskLoader } from '@/composeables';
import { TrackDao } from '@/dao';
import { $ti, $tm } from '@/locize';
import { MapTrack } from '@/objects/generated';
import { useEventCommonStoreRef, useEventStore, useToastStore } from '@/stores';
import { propertyof } from '@/utilities';
import { ref, watch, toRaw } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';

const { eventID } = useEventStore();

const { Event } = useEventCommonStoreRef();

const props = defineProps<{
    modelValue?: MapTrack[];

    updatetracks?: (tracks: MapTrack[]) => void;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: MapTrack[]): void;
}>();

const handleFileList = (files: File[]) => {
    if (files.length > 1) {
        // TODO: Show error message
        return;
    }
    uploadFile(files[0]);
};

// Handle to the grid component
const grid = ref<ComponentExposed<typeof GridComp<MapTrack>> | null>(null);

// Handle to the dropzone component
const dropzone = ref<ComponentExposed<typeof Dropzone> | null>(null);

const data = ref<MapTrack[]>([]);

const { AddLoader, LoadState } = useTaskLoader();

const currentOrganizerId = ref<number>(0);

const uploadFile = (file: File) => {
    AddLoader(async () => {
        // Create a FormData object to store the form data
        const formData = new FormData();
        formData.append('files', file);

        try {
            data.value = await TrackDao.UploadFiles(formData, eventID);

            // If there is no name set on the tracks, we set a default name i.e the event name with a counter
            for (let i = 0; i < data.value.length; i++) {
                if ((data.value[i].Name ?? '') == '') {
                    data.value[i].Name = Event.value?.Name ?? '';
                    if (i > 0) {
                        data.value[i].Name += ` (${i + 1})`; // Append a counter to the name
                    }
                }
            }

            setData();

            emit('update:modelValue', data.value);
        } catch (e) {
            useToastStore().addError($ti('trackingimport.invalidfile', { file: file.name }));

            // We reset the dropzone to allow for a new file to be uploaded
            dropzone.value?.reset();
        }

        // Refresh the grid
    }, 'loading.upload');
};

/**
 * Sets the dataset values and make sure we map in the correct organizer id and report back to the parent
 */
const setData = () => {
    if (currentOrganizerId.value > 0) {
        let _data = toRaw(data.value);
        // Set the organizer id for the tracks
        _data.forEach((d) => {
            d.OrganizerID = currentOrganizerId.value;
        });
        data.value = _data;
    }
    if (props.updatetracks) {
        props.updatetracks(data.value);
    }

    grid.value?.refreshCachedData();
};

// Watch for changes in the organizer id
watch(currentOrganizerId, () => setData());

// Watch for changes in the data
watch(data.value, () => setData());

// Watch for changes in the event id
watch(
    () => Event.value,
    () => {
        currentOrganizerId.value = Event.value?.OrganizerID ?? 0;
    },
    { immediate: true },
);

/**
 * Define the datasource for the grid
 */
const dataSource = new GridDataSource<any>({
    rowGetter: (p: IGridRowGetterParams<any>) => {
        p?.sort(data.value);

        const slice = data.value?.slice(p.startRow, p.endRow);

        return p.successCallback(slice, data.value, data.value.length);
    },
});

/**
 * Delete the selected rows
 */
const deleteSelected = () => {
    const selectedRows = grid.value?.getSelectedRows() ?? [];

    if (selectedRows.length === 0) {
        useToastStore().addError($tm('general.noselectedrows'));
        return;
    }

    data.value = data.value.filter((row) => !selectedRows.includes(row));

    // Update the parent
    setData();
};

/**
 * Define all the colums for the grid
 */
const columns: GridColumnOptions<MapTrack>[] = [];
columns.push(new GridColumnOptionsBuilder<MapTrack>(propertyof<MapTrack>('Name'), 'general.name').Build());
columns.push(
    new GridColumnOptionsBuilder<MapTrack>(propertyof<MapTrack>('Created'), 'general.created')
        .CellRenderer(DateComp, { format: 'DATETIMESHORT', monospace: false })
        .ReadOnly()
        .SortDisabled()
        .Build(),
);

columns.push(
    new GridColumnOptionsBuilder<MapTrack>(propertyof<MapTrack>('LineColor'), 'general.linecolor')
        .CellRenderer(DisplayColor)
        .SortDisabled()
        .Build(),
);

columns.push(
    new GridColumnOptionsBuilder<MapTrack>(propertyof<MapTrack>('TrackLength'), 'general.length')
        .CellRenderer(DisplayDistance)
        .ReadOnly()
        .SortDisabled()
        .Build(),
);

/**
 * Define the grid setup
 */
const gridSetup = new GridSetup<MapTrack>({
    rowIdGetter: (row: MapTrack) => row.Name ?? '',
    datasource: dataSource,
    hasCheckboxSelectionColumn: true,
    rowSelection: 'multiple',
    GridColumnDefinition: new GridColumnDefinition({ ColumnOptions: columns }),
});
</script>
